// Company list (decision 30.10.2026): companies with deals or activity; the search field in the header searches them
// by name, domain and place. Admins see possible duplicates above it (AB3), each with "zusammenführen".
import type { DuplicateGroup } from "../api";
import { loadCompanies, loadDuplicates, S } from "../state";
import { $, ago, emptyState, esc, toast } from "../util";
import { filterNote } from "./board";

let timer: ReturnType<typeof setTimeout> | undefined;
let loadingDuplicates = false;

const REASON: Record<DuplicateGroup["reasons"][number]["by"], (key: string) => string> = {
  domain: (k) => `Domain ${k}`,
  name: () => "gleicher Name",
  ust_idnr: (k) => `USt-IdNr. ${k}`,
};

function duplicates(): string {
  if (!S.me?.isAdmin) return "";
  if (S.duplicates === null) {
    if (!loadingDuplicates) {
      loadingDuplicates = true;
      void loadDuplicates().finally(() => {
        loadingDuplicates = false;
        if (S.view === "firmen") renderFirmen();
      });
    }
    return "";
  }
  if (!S.duplicates.length) return "";
  const n = S.duplicates.length;
  return `<details class="dups"><summary><b>Mögliche Dubletten</b> <small class="muted">${n} ${n === 1 ? "Gruppe" : "Gruppen"} mit gleicher Domain, gleichem Namen oder gleicher USt-IdNr.</small></summary>
    <ul class="rows">${S.duplicates
      .map(
        (g, i) => `<li><span>${g.companies.map((c) => `<a href="#firma-${esc(c.id)}">${esc(c.name)}</a>${c.deals ? ` <small class="muted">${c.deals} ${c.deals === 1 ? "Deal" : "Deals"}</small>` : ""}`).join(" · ")}
          <br><small class="muted">${esc(g.reasons.map((r) => REASON[r.by](r.key)).join(", "))}</small></span><button class="btn" data-dup="${i}">zusammenführen</button></li>`,
      )
      .join("")}</ul></details>`;
}

export function renderFirmen(): void {
  const el = $("#v-firmen");
  const n = S.companies?.length ?? 0;
  const found = n >= 300 ? "die ersten 300 Firmen" : `${n} ${n === 1 ? "Firma" : "Firmen"}`;
  const toolbar = `<div class="toolbar">${S.companyQuery.trim() && S.companies ? filterNote(S.companyQuery, found) : ""}
    <label><input type="checkbox" id="call" ${S.allCompanies ? "checked" : ""}> Auch Firmen ohne Deal und Aktivität</label></div>${duplicates()}`;
  if (!S.companies) {
    el.innerHTML = toolbar + emptyState("Lade Firmen …", "");
  } else if (!S.companies.length) {
    el.innerHTML = toolbar + emptyState("Keine Firma gefunden", "Andere Schreibweise oder die Domain versuchen.");
  } else {
    const deals = (o: number, all: number) => [o ? `${o} offen` : "", all > o ? `${all - o} abgeschl.` : ""].filter(Boolean).join(" · ");
    el.innerHTML =
      toolbar +
      `<div class="clist">${S.companies
        .map(
          (c) => `<a class="crow" href="#firma-${esc(c.id)}">
            <span class="cn"><b>${esc(c.name)}</b>${c.kind ? ` <span class="chip">${esc(c.kind)}</span>` : ""}</span>
            <span class="muted mono cd">${esc(c.domains[0] ?? "")}</span>
            <span class="muted">${esc([c.city, c.country].filter(Boolean).join(", "))}</span>
            <span>${deals(c.openDeals, c.deals)}</span>
            <span class="muted">${c.lastActivityAt ? ago(c.lastActivityAt) : "–"}</span></a>`,
        )
        .join("")}</div>${S.companies.length >= 300 ? `<p class="muted">Die ersten 300 Treffer. Bitte die Suche eingrenzen.</p>` : ""}`;
  }
  bind();
}

function bind(): void {
  const all = document.querySelector<HTMLInputElement>("#call");
  if (all) all.onchange = () => ((S.allCompanies = all.checked), void reload());
}

/** Typed into the header search while the list is open: searched after a pause. */
export function searchCompanies(): void {
  clearTimeout(timer);
  timer = setTimeout(() => void reload(), 250);
}

async function reload(): Promise<void> {
  try {
    await loadCompanies();
  } catch {
    toast("Die Firmen konnten nicht geladen werden.");
  }
  if (S.view === "firmen") renderFirmen();
}
